import { useState } from 'react'
import { ApiError } from '@/api/errors'
import type { TeamMember } from '@/api/pending'
import { Button, Caution, Modal, Pill, SummaryStrip, useToast } from '@/ui'
import { cx } from '@/ui/cx'
import { useRevokeScope } from './api'
import { revokeBlock, S, SCOPE_LABEL } from './strings'
import styles from './StaffView.module.css'

interface RevokeModalProps {
  condoId: number
  member: TeamMember
  team: TeamMember[]
  myUserId: number | undefined
  onClose: () => void
}

export function RevokeModal({ condoId, member, team, myUserId, onClose }: RevokeModalProps) {
  const toast = useToast()
  const revoke = useRevokeScope(condoId)
  const [typed, setTyped] = useState('')
  const matches = typed.trim().toLowerCase() === member.name.trim().toLowerCase()
  const serverCode = revoke.error instanceof ApiError ? revoke.error.code : undefined
  const block =
    revokeBlock(member, team, myUserId) ??
    (serverCode === 'cannot_revoke_self'
      ? 'self'
      : serverCode === 'last_admin'
        ? 'last_admin'
        : null)

  return (
    <Modal
      title={S.revokeTitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {S.keep}
          </Button>
          <Button
            variant="danger"
            icon="block"
            disabled={!matches || block !== null}
            loading={revoke.isPending}
            onClick={() =>
              revoke.mutate(member.scope_id, {
                onSuccess: () => {
                  toast(S.revoked(member.name))
                  onClose()
                },
              })
            }
          >
            {S.confirmRevoke}
          </Button>
        </>
      }
    >
      <SummaryStrip
        name={member.name}
        subtitle={member.email}
        aside={
          <Pill tone={member.scope === 'admin' ? 'brand' : 'neutral'}>
            {SCOPE_LABEL[member.scope]}
          </Pill>
        }
      />
      {block ? (
        <Caution icon="block">{block === 'self' ? S.revokeSelf : S.revokeLastAdmin}</Caution>
      ) : (
        <Caution icon="block">{S.revokeCaution(SCOPE_LABEL[member.scope])}</Caution>
      )}
      <div className={styles.typeBox}>
        <label htmlFor="revoke-name" className={styles.typeLabel}>
          {S.typePrefix} <strong>{member.name}</strong> {S.typeSuffix}
        </label>
        <input
          id="revoke-name"
          className={cx(styles.typeInput, typed && (matches ? styles.typeOk : styles.typeBad))}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={member.name}
          autoComplete="off"
          data-autofocus=""
        />
      </div>
      {revoke.isError && !block && (
        <p role="alert" className={styles.error}>
          {S.revokeError}
        </p>
      )}
    </Modal>
  )
}
